import { TimeoutError } from "./errors";

export class ThreadSafeBuffer {
  private buffer: number[] = [];
  private mutex: Promise<void> = Promise.resolve();
  private dataAvailableResolver: (() => void) | null = null;

  private acquireMutex(): Promise<() => void> {
    let release: () => void;
    const next = new Promise<void>((resolve) => { release = resolve; });
    const prev = this.mutex;
    this.mutex = next;
    return prev.then(() => release!);
  }


  /**
   * Appends data to the buffer.
   * @param data The data to append.
   */
  async append(data: Uint8Array): Promise<void> {
    const release = await this.acquireMutex();
    try {
      for (let i = 0; i < data.length; i++) {
        this.buffer.push(data[i]!);
      }
    } finally {
      release();
    }
    this.dataAvailableResolver?.();
    this.dataAvailableResolver = null;
  }

  /**
   * Clears the buffer. Should be called when the serial port is closed to ensure that pending read operations are unblocked.
   * No exceptions are thrown if there are pending read operations, as they will be unblocked and can handle the empty buffer case accordingly.
   */
  async clear(): Promise<void> {
    const release = await this.acquireMutex();
    try {
      this.buffer = [];
    } finally {
      release();
    }
  }

  /**
   *  Gets data from the buffer using the provided callback to determine how much data to consume. The callback should return null if there is not enough data in the buffer to produce a result, or an object containing the result and the number of bytes consumed if a result can be produced.
   * @param callback A function that determines how much data to consume from the buffer.
   * @returns The result produced by the callback, or null if there is not enough data in the buffer.
   */
  async get<T>(callback: (data: number[]) => { result: T; consumed: number } | null): Promise<T | null> {
    const release = await this.acquireMutex();
    try {
      const outcome = callback(this.buffer);
      if (outcome === null) {
        return null;
      }
      this.buffer = this.buffer.slice(outcome.consumed);
      return outcome.result;
    } finally {
      release();
    }
  }

  /**
 * Waits for data to be available in the buffer or until the specified timeout is reached.
 * @param timeoutMs The maximum time to wait in milliseconds.
 * @param abortController An AbortController to allow cancellation of the wait.
 * @returns A promise that resolves when data is available, or rejects with TimeoutError if timed out or aborted.
 */
  waitForData(timeoutMs: number, abortController: AbortController): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        reject(new TimeoutError('Read timed out'));
      }, timeoutMs);

      const cleanup = () => (this.dataAvailableResolver = null);

      const abortHandler = () => {
        clearTimeout(timer);
        cleanup();
        reject(new TimeoutError('Read aborted'));
      };

      this.dataAvailableResolver = () => {
        clearTimeout(timer);
        cleanup();
        abortController.signal.removeEventListener('abort', abortHandler);
        resolve();
      };

      abortController.signal.addEventListener('abort', abortHandler, { once: true });
    });
  }
}

export function findTerminatorInBuffer(buffer: number[], terminatorBytes: Uint8Array): number {
  for (let i = 0; i <= buffer.length - terminatorBytes.length; i++) {
    let found = true;
    for (let j = 0; j < terminatorBytes.length; j++) {
      if (buffer[i + j] !== terminatorBytes[j]) {
        found = false;
        break;
      }
    }
    if (found) {
      return i;
    }
  }
  return -1;
}
